import { defineStore } from 'pinia'
import EventService from '@/services/APIService.js'
import APIService from "../../services/APIService";

// ---------- Decimal helpers ----------
const DP = { NET: 4, GROSS: 2, QTY: 4 };
const pow10 = (dp) => (dp === 2 ? 100 : dp === 4 ? 10000 : Math.pow(10, dp));
const roundDp = (v, dp) => {
    const n = Number(v);
    const m = pow10(dp);
    return Math.round((n + Number.EPSILON) * m) / m;
};
const zeroIfTiny = (v, dp) => {
    const m = pow10(dp);
    const tol = 0.5 / m; // half ULP at target precision
    return Math.abs(v) < tol ? 0 : v;
};
// subtract with target precision and collapse tiny residuals
const subDp = (a, b, dp) => zeroIfTiny(roundDp(Number(a) - Number(b), dp), dp);

export const orderStore = defineStore('orderStore', {
    state: () => ({

        state: 'initializing',
        order_id: null,
        data: false,
        history: null,
        is_saving: false,
        is_loading: false,
        shipping_options: null,
        payment_options: null,
        update_payload: {},
        update_timeout: null,
        lines_changed: false,
        lines_saving: false,
        price_input_mode_is_net: false,
        refundable_lines: null,

        /**
         * What the order's shop may currently sell of each line, keyed by SKU. Filled by
         * loadLineStock(); an empty object simply means nothing has been asked yet.
         */
        stock_by_sku: {},
        show_payment_dialog: false,
        is_selected_cash_on_delivery_shipping_method: null

    }),

    getters: {

        getHistory () {

            if(this.history === null){return [];}
            return this.history.results;

        },

        getMaxResultsHistory () {

            if(this.history === null){return 0;}
            return this.history.results_max;

        },

        getCurrentPageHistory(){

            if(this.history === null){return 1;}
            return this.history.page;
        },

        /**
         * Whether what the shop can currently sell still says anything about this order.
         *
         * Only while it holds no claim on its goods: SaleEventStockHandler reserves nothing for
         * offer-request, proposal, preparing and pending, and from the next status on the line's own reservation
         * is already subtracted from the figure - so it would be reporting on the order's own
         * stock, and reading it as availability would be wrong twice over.
         */
        stockFiguresAreRelevant(state){

            return !!state.data && ['offer-request', 'proposal', 'preparing', 'pending'].indexOf(state.data.status) !== -1;

        },

        getTransitions(state){

            if(!state.data || !state.data['_actions']) { return []; }
            return state.data['_actions'].filter(act => act.action_type === 'transition');

        },

    },

    actions: {
        async init(order_id){
            let self = this;
            if(self.order_id !== order_id){
                self.$reset();
                self.order_id = order_id;

                await self.loadOrder();

                if(self.data === null) {
                    self.state = 'ready';
                    return;
                }

                if(self.data.date_paid === null && self.data.created_via !== 'shop'){
                    await self.refreshLinePrices();
                }

                self.loadOrder(['_actions','address','mods','validation','totals','shop','payment','user']).then(function(){
                    self.state = 'ready';
                });
            }
        },

        async reloadOrder(){
            let self = this;
            return self.loadOrder(['_actions','address','mods','validation','totals','shop','payment','user','line_items']).then(function(){
                self.state = 'ready';
            });
        },

        async validateOrder(){
            let self = this;
            return EventService.get('/api/v3/order/'+self.order_id+'/validate').then(function(response){
                self.data.validation = response.data.payload;
            });
        },

        async refreshLinePrices(keep_prices = true, update_timestamps = false){

            let self = this;
            return EventService.patch('/api/v3/order/'+self.order_id+'/lines/refresh',{keep_prices: keep_prices, update_timestamps: update_timestamps});

        },

        async loadOrder(expand = null){
            let self = this;
            self.is_loading = true;
            if(expand === null){ expand = []; }
            expand.push('_actions'); expand.push('validation'); expand.push('mods');
            // de-dupe expansions
            const exp = Array.from(new Set(expand));
            return EventService.post('/api/v3/order/'+self.order_id,{expand:exp}).then(function(response){
                self.data = Object.assign({}, self.data, response.data.payload);
                self.is_loading = false;

                // Only worth asking once the lines are actually here, and worth re-asking every
                // time they are: adding a line is exactly when its availability becomes a question.
                if(exp.indexOf('line_items') !== -1){ self.loadLineStock(); }
            }).catch(function(){
                self.data = null;
                self.is_loading = false;
            });
        },

        /**
         * What the shop this order belongs to may currently sell of each of its lines.
         *
         * One request for the whole order rather than one per line: the endpoint reads the pool
         * for the whole list at once, so a 500-line order costs about as much as a short one.
         * Informational only - nothing here decides what may be ordered, it just saves opening
         * each article to find out.
         */
        async loadLineStock(){

            let self = this;

            // Placed orders are not asking the question any more - see stockFiguresAreRelevant().
            if(!self.stockFiguresAreRelevant){ self.stock_by_sku = {}; return; }

            const lines = (self.data && self.data.line_items) || [];
            // A set's components too: whether the set can be put together is decided by them.
            const skus = [...new Set(lines.filter(l => l.type === 'product').flatMap(l => [
                l.sku,
                ...(l.components || []).map(component => component.sku),
            ]).filter(Boolean))];

            if(skus.length === 0){ self.stock_by_sku = {}; return; }

            const shop = self.data && self.data.shop ? self.data.shop.slug : null;
            // The method decides which warehouses count: pickup stock for a pickup order,
            // shipping stock otherwise. Without one the endpoint answers the plain shop figure.
            const shipping_method = self.data && self.data.shipping_method_id ? self.data.shipping_method_id : null;

            const body = {skus};
            if(shop){ body.shop = shop; }
            if(shop && shipping_method){ body.shipping_method = shipping_method; }

            return EventService.post('/api/v3/product/stock', body).then(function(response){

                const by_sku = {};

                for(const row of (response.data.payload.results || [])){
                    if(row.found){ by_sku[row.sku] = row; }
                }

                self.stock_by_sku = by_sku;

            }).catch(function(){

                // A figure nobody could fetch is shown as nothing at all - the order screen has
                // work to do that does not depend on it.
                self.stock_by_sku = {};

            });
        },

        async loadOrderContents(){
            let self = this;
            return self.loadOrder(['line_items']);
        },

        async loadOrderHistory(page = 1){


            let self = this;
            const params = {
                object: 'order-event',
                scope:'results',
                params:{
                    expand: ['user'],
                    page_size: 10,
                    page: page,
                    query: {
                        order_id: [self.order_id]
                    }
                },

            };
            return EventService.post('/api/v3/search', params).then(function(response) {

                if (self.history !== null && page !== 1) {
                    // Alte Ergebnisse oben anfügen
                    response.data.payload.results = [
                        ...self.history.results,
                        ...response.data.payload.results
                    ];
                }

                self.history = response.data.payload;

                return self.history;
            });


        },



        async addItem(item){
            return this.addItems([item]);
        },

        async addItems(data) {
            let self = this;
            return EventService.put('/api/v3/order/' + self.order_id + '/lines', {lines:data, expand: ['item-product']}).then(function(response){
                // authoritative refresh to avoid duplicates/out-of-sync — awaited so callers
                // only resolve once fresh line_items are in (avoids UI reverting to stale state)
                return Promise.all([
                    self.loadOrder(['line_items','totals']),
                    self.loadShippingOptions(),
                    self.loadPaymentOptions()
                ]).then(function(){
                    return response.data.payload;
                });
            });
        },

        updateItem(line){
            let self = this;
            if(!self.update_payload.lines){ self.update_payload.lines = []; }
            let index = self.update_payload.lines.findIndex(ln => ln.id === line.id);
            if(index !== -1){
                self.update_payload.lines.splice(index,1);
            }
            self.update_payload.lines.push(line);

            self.lines_changed = true;
            if(!self.is_saving){
                clearTimeout(self.update_timeout);
                self.update_timeout = setTimeout(function(){
                    self.executeOrderUpdate();
                },600);
            }
        },

        executeOrderUpdate(prevUpdatedIds = []){
            let self = this;
            let payload = JSON.parse(JSON.stringify(self.update_payload));
            // Accumulate IDs across the entire recursive save chain so the final
            // reload knows which lines to refresh.
            const updatedLineIds = [...prevUpdatedIds, ...(payload.lines || []).map(l => l.id)];
            self.update_payload = {};
            self.is_saving = true;

            if(payload.lines){ self.lines_saving = true; }

            return EventService.patch('/api/v3/order/' + self.order_id + '/lines',payload).then(function(response){
                // if new changes buffered while saving, run again immediately
                const hasBuffered = !!(self.update_payload && self.update_payload.lines && self.update_payload.lines.length);
                self.is_saving = false;

                if(hasBuffered){
                    return self.executeOrderUpdate(updatedLineIds);
                }

                // All saves done — reload only the changed lines in-place so unrelated
                // rows don't re-render and active inputs don't lose their value.
                if(self.lines_saving){ self.lines_saving = false; self.lines_changed = false; }
                self.reloadLines(updatedLineIds);
            });
        },

        async reloadLines(ids){
            let self = this;
            return EventService.post('/api/v3/order/' + self.order_id, {expand: ['line_items', '_actions', 'validation', 'mods']}).then(function(response){
                const payload = response.data.payload;
                if(!payload || !self.data || !self.data.line_items) return;

                // Merge only the lines that were saved — in-place to avoid re-rendering the whole table
                const freshLines = payload.line_items || [];
                ids.forEach(id => {
                    const fresh = freshLines.find(l => l.id === id);
                    if(fresh){
                        const idx = self.data.line_items.findIndex(l => l.id === id);
                        if(idx !== -1) self.data.line_items.splice(idx, 1, fresh);
                    }
                });

                // payment-method-fee and shipping-additional-fee lines are recalculated
                // server-side (COD fee, shipping additional fees, ...), so always sync them
                // fully (add, update, or remove) after any line save, since their id is never
                // in `ids`. Scoped to our subtype so manually-added 'fee' lines aren't touched.
                const isServerFee = l => l.type === 'payment-method-fee'
                    || (l.type === 'fee' && (l.subtype === 'shipping-additional-fee-mandatory' || l.subtype === 'shipping-additional-fee-optional'));
                const freshFees = freshLines.filter(isServerFee);
                // Remove stale local fees that no longer exist on the server
                for(let i = self.data.line_items.length - 1; i >= 0; i--){
                    if(isServerFee(self.data.line_items[i])){
                        const stillExists = freshFees.find(l => l.id === self.data.line_items[i].id);
                        if(!stillExists) self.data.line_items.splice(i, 1);
                    }
                }
                // Update existing fees and append new ones
                freshFees.forEach(fee => {
                    const idx = self.data.line_items.findIndex(l => l.id === fee.id);
                    if(idx !== -1) self.data.line_items.splice(idx, 1, fee);
                    else self.data.line_items.push(fee);
                });

                // Update order-level data (totals, actions, validation, etc.) without touching line_items
                const withoutLines = Object.assign({}, payload);
                delete withoutLines.line_items;
                self.data = Object.assign({}, self.data, withoutLines);

                // A changed quantity can change what a set line consists of - other packs, other
                // components - so their availability has to be asked again, not just kept.
                self.loadLineStock();
            });
        },

        /** Applies a manual line order: `ids` are the shown lines, top to bottom. */
        async reorderLines(ids){
            let self = this;

            // The table sorts by menu_order, so this is all the local update there is.
            ids.forEach((id, i) => {
                const line = self.data.line_items.find(l => l.id === id);
                if(line){ line.menu_order = i + 1; }
            });

            self.lines_changed = true;
            self.lines_saving = true;

            return EventService.patch('/api/v3/order/' + self.order_id + '/lines/order', {line_ids: ids}).catch(function(error){
                self.loadOrder(['line_items']);
                throw error;
            }).finally(function(){
                self.lines_saving = false;
                self.lines_changed = false;
            });
        },

        async removeItem(id){
            let self = this;
            let index = self.data.line_items.findIndex(itm => itm.id === id)
            return EventService.delete('/api/v3/order/' + self.order_id + '/lines/'+id).then(function(response){
                if(response.data.payload === true){
                    self.data.line_items.splice(index,1);
                    self.loadOrder(['line_items','totals']);
                    self.loadShippingOptions();
                    self.loadPaymentOptions();
                }
            }).catch(function(error){

                if(error?.response?.status === 404){
                    if(index !== -1){ self.data.line_items.splice(index,1); }
                    return Promise.all([
                        self.loadOrder(['line_items','totals']),
                        self.loadShippingOptions(),
                        self.loadPaymentOptions()
                    ]);
                }
                throw error;
            });
        },

        async removeItems(ids){
            let self = this;
            if(!ids || !ids.length){ return Promise.resolve(); }
            return EventService.delete('/api/v3/order/' + self.order_id + '/lines', {params: {line_ids: ids}}).then(function(){
                // reload regardless of payload true/false — false just means some/all ids were
                // already gone (unknown id or blocked by mods), reload still gets local state back in sync
                ids.forEach(function(id){
                    let index = self.data.line_items.findIndex(itm => itm.id === id);
                    if(index !== -1){ self.data.line_items.splice(index,1); }
                });
                return Promise.all([
                    self.loadOrder(['line_items','totals']),
                    self.loadShippingOptions(),
                    self.loadPaymentOptions()
                ]);
            }).catch(function(error){
                if(error?.response?.status === 404){
                    ids.forEach(function(id){
                        let index = self.data.line_items.findIndex(itm => itm.id === id);
                        if(index !== -1){ self.data.line_items.splice(index,1); }
                    });
                    return Promise.all([
                        self.loadOrder(['line_items','totals']),
                        self.loadShippingOptions(),
                        self.loadPaymentOptions()
                    ]);
                }
                throw error;
            });
        },

        async updateOrder(data){
            let self = this;
            self.is_saving = true;
            // finally: a failed save must not leave is_saving set, or line edits stay buffered for good.
            return EventService.patch('/api/v3/order/' + self.order_id,data).finally(function(){
                self.is_saving = false;
            });
        },

        async loadPaymentOptions(){
            let self = this;
            return EventService.get('/api/v3/order/' + self.order_id+'/payment-options').then(function(response){
                self.payment_options = response.data.payload;
            });
        },

        async loadShippingOptions(){
            let self = this;
            return EventService.get('/api/v3/order/' + self.order_id+'/shipping-options').then(function(response){
                self.shipping_options = response.data.payload;
                if(self.data && self.data.shipping_method_id && Array.isArray(self.shipping_options)){
                    const current = self.shipping_options.find(m => m.id === self.data.shipping_method_id);
                    if(current !== undefined){
                        self.is_selected_cash_on_delivery_shipping_method = current.is_cash_on_delivery ?? false;
                    }
                }
            });
        },

        async loadRefundableItems(for_full_refund = false) {
            const self = this;

            await self.loadOrderContents();

            // --- Helpers -------------------------------------------------------------
            const toNum  = (v) => Number(v ?? 0);
            const absNum = (v) => Math.abs(toNum(v));

            // dp-sichere Differenz als Cap (mit korrekter Behandlung von negativen Fees)
            const remain = (total, refunded, dp) => {
                // Beispiel Rabatt: total = -0.10, refunded = 0  -> -0.10 (Rabatt wird rückgängig gemacht)
                // Beispiel Produkt: total = 5.00,  refunded = 0  -> 5.00 - 0 = 5.00
                // Beispiel Fee: total = 2.00,  refunded = 0  -> 2.00 - 0 = 2.00

                // Für negative Beträge (Rabatte/Skonto): Differenz mit korrektem Vorzeichen
                // Wenn total = -0.10 und refunded = 0, dann bleibt -0.10 übrig
                // Wenn total = -0.10 und refunded = -0.10, dann ist 0 übrig
                const r = subDp(total, refunded, dp);

                // Negative Werte beibehalten (für Rabatte), positive nur wenn > 0
                if (total < 0) {
                    return r < 0 ? r : 0;
                } else {
                    return r > 0 ? r : 0;
                }
            };

            const src = Array.isArray(self.data?.line_items) ? self.data.line_items : [];
            const items = typeof structuredClone === 'function'
                ? structuredClone(src)
                : src.map(i => ({ ...i }));

            /**
             * Goods that never left the building cannot come back to it. A line is therefore
             * offered twice when it is part shipped: what went out can be returned to a bin,
             * and what is still on the shelf can only be cancelled, which just ends the claim.
             *
             * The money cap is split with it, and deliberately not by rounding both halves —
             * the table refunds max_total verbatim when the entered quantity equals a row's
             * max_quantity, so two independently rounded halves could refund a cent more or
             * less than the line is worth. The return row takes its share, the cancel row takes
             * whatever is left.
             */
            const splitRefundable = (item, max_quantity, max_total, max_total_including_tax) => {
                // What the customer actually got. quantity_handed_over is quantity_shipped for a
                // shipped order and the whole line for a collected pickup order, which is never
                // parcelled and so reads as 0 parcels for ever. Falls back to the parcel count
                // for a payload that predates the field.
                const shipped   = toNum(item.quantity_handed_over ?? item.quantity_shipped);
                const takenBack = toNum(item.quantity_taken_back);

                // What actually left, which is not the same as what was parcelled. A virtual
                // article with managed stock is dispatched by a stock issue and never by a parcel,
                // so its quantity_shipped stays 0 for ever while the goods are genuinely gone —
                // counting parcels offered the whole line as a cancellation and left the stock
                // written off. quantity_dispatched is the warehouse's own answer, already net of
                // anything booked back.
                //
                // Null means the line has no warehouse history at all — the great majority of
                // older orders, shipped before their shop kept records, and every order of a shop
                // whose articles live only in warehouses that keep no numbers. There the handover
                // above is the only thing we know, and it is the right answer to fall back to:
                // those lines are genuinely returnable, they simply cannot be booked into a bin,
                // which is what orderItemTable decides separately from dispatched_warehouses.
                //
                // Netted off with quantity_taken_back rather than quantity_returned: the latter
                // counts cancellations too, so cancelling the unshipped part of a line used to
                // shrink this row to nothing on the very next refund. quantity_taken_back only
                // counts refunds coded RETURN — goods that genuinely came back.
                const dispatched = item.quantity_dispatched === null || item.quantity_dispatched === undefined
                    ? Math.max(0, shipped - takenBack)
                    : toNum(item.quantity_dispatched);

                const returnable  = Math.max(0, Math.min(max_quantity, dispatched));
                const cancellable = Math.max(0, max_quantity - returnable);

                if (returnable <= 0 || cancellable <= 0) {
                    // Wholly one kind or the other; no split, no rounding to get wrong.
                    return [{
                        kind: returnable > 0 ? 'return' : 'cancel',
                        max_quantity,
                        max_total,
                        max_total_including_tax,
                    }];
                }

                // roundDp/subDp are this file's own money helpers (defined at the top). Do not
                // reach for decimal.js here — orderStore.js does not use it, and mixing the two
                // rounding regimes in one calculation is how cents go missing.
                const share = (amount, dp) => roundDp(Number(amount) * returnable / max_quantity, dp);

                const return_net   = share(max_total, DP.NET);
                const return_gross = share(max_total_including_tax, DP.GROSS);

                return [
                    {
                        kind: 'return',
                        max_quantity: returnable,
                        max_total: return_net,
                        max_total_including_tax: return_gross,
                    },
                    {
                        kind: 'cancel',
                        max_quantity: cancellable,
                        max_total: subDp(max_total, return_net, DP.NET),
                        max_total_including_tax: subDp(max_total_including_tax, return_gross, DP.GROSS),
                    },
                ];
            };

            const refundable_items = [];
            const rates = [];

            items.forEach((item) => {
                // Rohwerte absichern
                const total                 = toNum(item.total);
                const refund_total          = toNum(item.refund_total);
                const total_incl_tax        = toNum(item.total_including_tax);
                const refund_total_incl_tax = toNum(item.refund_total_including_tax);
                const quantity              = toNum(item.quantity);
                const quantity_returned     = toNum(item.quantity_returned);

                // POSITIVE Caps (unabhängig vom Ursprungs-Vorzeichen)
                const max_total               = remain(total,          refund_total,          DP.NET);   // 4 dp
                const max_total_including_tax = remain(total_incl_tax, refund_total_incl_tax, DP.GROSS); // 2 dp
                const max_quantity            = remain(quantity,       quantity_returned,     DP.QTY);   // 4 dp

                // editierbare Felder initial leer/0
                item.total = 0;
                item.total_tax = 0;
                item.total_including_tax = 0;
                item.quantity = 0;
                item.is_return = false;

                // Caps anhängen
                item.max_total = max_total;
                item.max_total_including_tax = max_total_including_tax;
                item.max_quantity = max_quantity;

                // Special handling for voucher products: max refundable = remaining voucher value
                if (item.is_voucher_product && item.voucher_total_remaining_value !== undefined) {
                    // Override max_total with remaining voucher value (Wertersatz!)
                    item.max_total = item.voucher_total_remaining_value;
                    item.max_total_including_tax = item.voucher_total_remaining_value;
                }

                // Optional: Full-Refund
                if (for_full_refund) {
                    if (item.type === 'product') {
                        // Produkte: Menge setzen
                        item.quantity = max_quantity;
                    } else {
                        // Nicht-Produkte (shipping, fees, etc.): Beträge setzen
                        item.total = max_total;
                        item.total_including_tax = max_total_including_tax;
                        // Steuer berechnen
                        item.total_tax = subDp(max_total_including_tax, max_total, DP.NET);
                    }
                }

                // Aufnahme ins UI:
                // - Produkte: wenn Menge > 0 ODER ein (Netto/Brutto)-Budget existiert
                // - Nicht-Produkte (fees, shipping, payment-method-fee, discounts):
                //   sobald Netto- oder Brutto-Cap >= Schwelle (auch negative Werte für Rabatte)
                // - Coupons und Vouchers werden ausgeschlossen (keine Rückerstattung von Gutscheinen)
                const hasQty         = item.type === 'product' && max_quantity > 0;
                const hasNetBudget   = Math.abs(max_total) >= 0.01;
                const hasGrossBudget = Math.abs(max_total_including_tax) >= 0.01;
                const isCouponOrVoucher = item.type === 'coupon' || item.type === 'multipurpose-voucher';

                if ((hasQty || hasNetBudget || hasGrossBudget) && !isCouponOrVoucher) {

                    // Only products are shipped, so only products can be part-shipped. Everything
                    // else — shipping, fees, discounts — stays one row and carries no kind.
                    const parts = item.type === 'product'
                        ? splitRefundable(item, max_quantity, item.max_total, item.max_total_including_tax)
                        : [{ kind: null, max_quantity, max_total: item.max_total, max_total_including_tax: item.max_total_including_tax }];

                    parts.forEach((part, index) => {
                        const row = Object.assign({}, item, {
                            refund_kind: part.kind,
                            max_quantity: part.max_quantity,
                            max_total: part.max_total,
                            max_total_including_tax: part.max_total_including_tax,
                        });

                        // Vuetify keys rows by this, and two rows now share an order line id.
                        row.row_key = part.kind === null ? String(item.id) : item.id + ':' + part.kind;

                        if (for_full_refund && item.type === 'product') {
                            row.quantity = part.max_quantity;
                        }

                        refundable_items.push(row);
                    });

                    if(rates.indexOf(item.tax_rate) === -1){
                        rates.push(item.tax_rate);
                    }

                }

            });

            // --- Drift-Korrektur für max_total und max_total_including_tax --------------
            // Stelle sicher, dass die Summe aller max_total und max_total_including_tax
            // exakt dem erstattbaren Betrag entspricht, um Rundungsfehler zu vermeiden

            // Berechne den tatsächlich erstattbaren Betrag
            // BRUTTO: Order Total + Voucher - bereits erstattet
            const orderTotalGross = toNum(self.data?.total);
            const voucherTotal = toNum(self.data?.voucher_total);
            const alreadyRefundedGross = toNum(self.data?.refund_total_including_tax);
            const actualRefundableGross = roundDp(orderTotalGross + voucherTotal - alreadyRefundedGross, DP.GROSS);

            // NETTO: Order Total (exkl. Tax) - bereits erstattet
            const orderTotalNet = toNum(self.data?.total_excluding_tax);
            const alreadyRefundedNet = toNum(self.data?.refund_total);
            const actualRefundableNet = roundDp(orderTotalNet - alreadyRefundedNet, DP.NET);

            // Berechne die Summe aller max_total und max_total_including_tax Werte
            let sumMaxGross = 0;
            let sumMaxNet = 0;
            refundable_items.forEach(item => {
                sumMaxGross = roundDp(sumMaxGross + toNum(item.max_total_including_tax), DP.GROSS);
                sumMaxNet = roundDp(sumMaxNet + toNum(item.max_total), DP.NET);
            });

            // Berechne die Drift (Differenz zwischen Soll und Ist) für BEIDE Werte
            const driftGross = roundDp(actualRefundableGross - sumMaxGross, DP.GROSS);
            const driftNet = roundDp(actualRefundableNet - sumMaxNet, DP.NET);

            // Wenn eine signifikante Drift existiert (>= 0.001€), korrigiere das teuerste Item
            if (Math.abs(driftGross) >= 0.001 || Math.abs(driftNet) >= 0.001) {
                // Finde das teuerste refundable Item (nicht manual_refund)
                let largestItem = null;
                let largestGross = 0;

                refundable_items.forEach(item => {
                    if (item.type !== 'manual_refund') {
                        const gross = absNum(item.max_total_including_tax);
                        if (gross > largestGross) {
                            largestGross = gross;
                            largestItem = item;
                        }
                    }
                });

                // Korrigiere das teuerste Item um die Drift (BEIDE Werte separat)
                if (largestItem) {
                    const oldGross = toNum(largestItem.max_total_including_tax);
                    const oldNet = toNum(largestItem.max_total);

                    // Wende Drift an (mit korrektem Vorzeichen) - SEPARAT für Netto und Brutto
                    const correctedGross = roundDp(oldGross + driftGross, DP.GROSS);
                    const correctedNet = roundDp(oldNet + driftNet, DP.NET);

                    largestItem.max_total_including_tax = correctedGross;
                    largestItem.max_total = correctedNet;

                    console.log('[Drift-Korrektur] Differenz: Brutto', driftGross, '€, Netto', driftNet, '€ wurde auf Item', largestItem.id, '('+largestItem.name+') angewendet');
                    console.log('[Drift-Korrektur] Neu: max_total_including_tax:', correctedGross, '€ (vorher:', oldGross, '€), max_total:', correctedNet, '€ (vorher:', oldNet, '€)');
                }
            }

            // --- Ende Drift-Korrektur -------------------------------------------------

            rates.forEach(function(rate){

                refundable_items.push(

                    {
                        id: `manual_refund_${rate}`,  // Unique ID for manual refund lines
                        row_key: `manual_refund_${rate}`,
                        name: `Manuelle Rückerstattung (${rate}%)`,
                        type: 'manual_refund',
                        is_refundable: true,
                        tax_rate: rate,
                        // editierbare Felder (UI mit POSITIVEN Werten; beim Buchen negativ senden)
                        total: 0,
                        total_including_tax: 0,
                        total_tax: 0,
                        price: 0,
                        price_including_tax: 0,
                        quantity: 0,
                        // Caps je Bucket
                        max_total: null,                   // NETTO (4 dp)
                        max_total_including_tax: null,   // BRUTTO (2 dp)
                        max_quantity: 1,
                        is_return: false
                    }

                );

            })


            self.refundable_lines = refundable_items;

        },

        async createRefund(data){
            let self = this;
            return APIService.put('/api/v3/orders/' + self.order_id+'/refund',data).then(function(response){
                self.loadOrder().then(function(){
                    self.loadRefundableItems();
                });
            });
        },

        async confirmAllRefunds(){

            let self = this;

            return APIService.post('/api/v3/orders/' + self.order_id+'/refund/confirm').then(function(response){
                self.loadOrder();
                self.loadOrderHistory();
            });
        },

        processVatStatus(status){

            let is_vat_exempt = false;

            if(status === null){

                is_vat_exempt = false;

            } else if(status.is_valid === true) {

                is_vat_exempt = true;
            }

            if(this.data.is_vat_exempt !== is_vat_exempt){

                this.reloadOrder();

            }

        },
    }
})